import React, { useCallback, useMemo, useState } from "react";
import { FormControl, OutlinedInput, InputLabel, Button, Alert } from "@mui/material";
import Grid from '@mui/material/Grid2';
import { User } from "@models";
import DandDAPI from "@dndUtilities/system_api";

const api = DandDAPI.getInstance();

type Mode = "login" | "register" | "forgot" | "reset";

interface Props {
}

async function hashPassword(password: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(password);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, "0")).join("");
}

export default function Login({ }: Props) {
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");

  const [processing, setProcessing] = useState(false);
  const [messages, setMessages] = useState<string[]>([]);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);

  const resetToLogin = useCallback(() => {
    setProcessing(false);
    setMessages([]);
    setErrorMessages([]);
  }, []);

  const switchToRegister = useCallback(() => {
    setProcessing(false);
    setUsername("");
    setMessages([]);
    setErrorMessages([]);
  }, []);

  // ── Login / Register ──────────────────────────────────────────────────────

  const login = useCallback(async () => {

    setProcessing(true);
    setMessages([]);
    setErrorMessages([]);

      setMessages(["Logging in..."]);
      try {
        const res = await api.loginOld({ email });
        if (res && res.body.error === undefined && !res.body.message && res.res instanceof User) {
          window.location.reload();
        } else {
          setErrorMessages(["Login failed. Check your credentials."]);
        }
      } catch (error: any) {
        setErrorMessages([`Login error: ${error.message}`]);
      } finally {
        setProcessing(false);
      }
  }, [username, email, processing]);

  // ── Render ────────────────────────────────────────────────────────────────

  const headerText: Record<Mode, string> = {
    login: "Log In",
    register: "Register",
    forgot: "Forgot Password",
    reset: "Enter Reset Code",
  };

  const isSubmitDisabled =
    processing || email === "";

  const renderLoginRegisterView = useMemo(() => (
    <Grid container spacing={3} direction="column">
      <Grid>
        <FormControl variant="outlined" fullWidth>
          <InputLabel htmlFor="text_field_login_email">Email</InputLabel>
          <OutlinedInput
            id="text_field_login_email"
            type="text"
            autoComplete="off"
            value={email}
            onChange={e => setEmail(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && !isSubmitDisabled) login(); }}
            label="Email"
            fullWidth
          />
        </FormControl>
      </Grid>
      <Grid container spacing={2} justifyContent="center">
        <Grid>
          <Button
            disabled={isSubmitDisabled}
            onClick={login}
            variant="contained"
            color="primary"
            size="large"
          >
            {processing
              ? "Logging In..."
              : "Submit"}
          </Button>
        </Grid>
      </Grid>
    </Grid>
  ), [username, email, processing, isSubmitDisabled, login]);

  if (!api.online) {
    return <div>Login Unavailable when offline.</div>;
  }

  return (
    <div className="padded">
      <div className="header">
        Welcome Adventurer! Please <span>Login</span>
      </div>

      {renderLoginRegisterView}

      {errorMessages.length > 0 && (
        <Grid container spacing={1} direction="column" style={{ marginTop: 16 }}>
          {errorMessages.map((msg, i) => (
            <Grid key={i}><Alert severity="error">{msg}</Alert></Grid>
          ))}
        </Grid>
      )}

      {messages.length > 0 && (
        <Grid container spacing={0} direction="column" style={{ marginTop: 16 }}>
          <span className="header">Status Log:</span>
          {messages.map((msg, i) => (
            <Grid key={i} className="padded">
              <span>[{i}]</span> {msg}
            </Grid>
          ))}
        </Grid>
      )}
    </div>
  );
}